import { getInfCLogger } from '@infodata-group/inf-commons-core';
import { DatePipe, DecimalPipe } from '@angular/common';
import dayjs, { OpUnitType } from 'dayjs';
import { DataModel } from '../engine/data-model';
import { ModelProperty } from '../engine/model-property';
import { DateTime } from '../model/date-time';
import { Selectable } from '../interface/selectable';
import { GroupEntities } from '../model/entity/group-entities';

import isBetween from 'dayjs/plugin/isBetween';
import isoWeek from 'dayjs/plugin/isoWeek';
import isoWeeksInYear from 'dayjs/plugin/isoWeeksInYear';
import isLeapYear from 'dayjs/plugin/isLeapYear';

import * as uuid from 'uuid';
import { ColorType } from '../types/color.type';
import { Time } from '../types/time.type';
import { CheckState } from '../enum/check-state.enum';

const logger = getInfCLogger('CommonUtils');

dayjs.extend(isBetween);
dayjs.extend(isoWeek);
dayjs.extend(isoWeeksInYear);
dayjs.extend(isLeapYear);

export class CommonUtils {
  static pipe = new DatePipe('fr');

  /**
   * Convert string format dd/mm/yyyy to javascript date object.
   * if date isn't in correct format, return null
   * @param str date on dd/mm/yyyy format
   */
  public static convertStrToDate(str: string): Date {
    if (str) {
      const array = str.split('/');
      if (array.length === 3) {
        return new Date(
          parseInt(array[2], 10),
          parseInt(array[1], 10) - 1,
          parseInt(array[0], 10),
        );
      } else {
        return null;
      }
    } else {
      return null;
    }
  }

  public static convertStrToDateTime(date: string, time: string): Date {
    try {
      return new Date(
        this.convertStrToDate(date).getTime() +
          this.convertStrToTimeInMillis(time),
      );
    } catch (e) {
      logger.error(`Impossible to convert "${date} ${time}" to a date`, e);
      return null;
    }
  }

  static convertStrToTime(defaultTime: string): Time {
    const split = defaultTime.split(':');
    if (split.length > 1) {
      const h = parseInt(split[0], 10);
      const m = parseInt(split[1], 10);
      return { hours: h, minutes: m };
    }
    return undefined;
  }

  /**
   * Convert decimal System number to Hexa string
   * @param n number or string number
   * @return hexadecimal String number
   */
  public static convertDecimalNumberToHexa(n: number | string): string {
    let hex = Number(n).toString(16);
    if (hex.length < 2) {
      hex = '0' + hex;
    }
    return hex;
  }

  /**
   * Convert Js date object to a date string format dd/mm/yyyy
   * @param date js Date Object
   */
  public static convertDateToString(date: Date): string {
    return this.pipe.transform(date, 'dd/MM/yyyy');
  }

  static convertTimeToString(time: Time) {
    const pipe = new DecimalPipe('fr');
    return `${pipe.transform(time.hours, '2.0-0')}:${pipe.transform(
      time.minutes,
      '2.0-0',
    )}`;
  }

  static convertDateToTimeString(date: Date, second?: boolean): string {
    if (second) {
      return this.pipe.transform(date, 'HH:mm:ss');
    } else {
      return this.pipe.transform(date, 'HH:mm');
    }
  }

  /**
   * Convert string date format hh:mm:ss to time in millis
   * @param str time format hh:mm:ss
   */
  public static convertStrToTimeInMillis(str: string): number {
    const array = str.split(':');
    return (
      parseInt(array[0], 10) * 3600 * 1000 +
      parseInt(array[1], 10) * 60 * 1000 +
      (array.length === 3 ? parseInt(array[2], 10) * 1000 : 0)
    );
  }

  public static convertMilliToValue(n: number, type: 'day'): number {
    if (type === 'day') {
      const diffTime = Math.abs(n);
      return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    } else {
      return n;
    }
  }

  public static normalize(str: string, lowerCase = false): string {
    const result = (str + '').normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    if (lowerCase) {
      return result.toLowerCase();
    } else {
      return result;
    }
  }

  public static normalizePath(path: string) {
    if (!path.startsWith('/')) {
      return `/${path}`;
    }
    return path;
  }

  public static serializeData(model: DataModel, o: any) {
    const data: any = {};
    // tslint:disable-next-line:forin
    for (const field in o) {
      const prop = model.find(field);
      const v = o[field];
      if (v instanceof Array) {
        data[prop.id] = v.map((x) => this.getValue(prop, x));
      } else {
        data[prop.id] = this.getValue(prop, v);
      }
    }
    return data;
  }

  public static getValue(prop: ModelProperty, v: any): any {
    if (prop && v) {
      const key = prop.getPrimaryKey();
      const labelKey = prop.getLabelKey() ? prop.getLabelKey()[0] : null;
      if (key) {
        const d = {};
        if (v.getId) {
          d[key] = v.getId();
          if (labelKey) {
            d[labelKey] = v.getLabel();
          }
        } else {
          d[key] = v;
        }
        return d;
      } else {
        if (v.getId) {
          return v.getId();
        }
        if (v instanceof Date) {
          return this.convertDateToString(v);
        }
        if (v instanceof DateTime) {
          return v.serialize();
        }
        return v;
      }
    }
  }

  public static convertToGroup<T extends Selectable>(
    entities: T[],
    groupField: string,
    groupLabelField?: string,
  ): GroupEntities<T>[] {
    const groups: GroupEntities<T>[] = [];
    for (const e of entities) {
      const name = e[groupField];
      let label = name;
      if (groupLabelField && e[groupLabelField]) {
        label = `${name} - ${e[groupLabelField]}`;
      }
      let group = groups.find((gr) => gr.id === name);
      if (!group) {
        group = new GroupEntities<any>(name, label, [e]);
        groups.push(group);
        continue;
      }
      group.push(e);
    }
    return groups;
  }

  public static compare(comparant: any, comparant2: any): number {
    if (typeof comparant !== typeof comparant2) {
      console.warn('not the same type');
      return 0;
    }
    if (typeof comparant === 'string') {
      return comparant.localeCompare(comparant2);
    }
    if (typeof comparant2 === 'number') {
      // Todo :: wtf ????
      return comparant === comparant2 ? 0 : -1;
    }
    if (comparant instanceof Array) {
      return comparant.join(',').localeCompare(comparant2.join(','));
    }
    if (comparant instanceof Object) {
      return comparant.toString().localeCompare(comparant2.toString());
    }
    return 0;
  }

  static startOfTheWeek(date: Date): Date {
    const m = dayjs(date);
    return m.startOf('isoWeek').toDate();
  }

  static endOfTheWeek(date: Date): Date {
    const m = dayjs(date);
    return m.endOf('isoWeek').toDate();
  }

  static startOfTheMonth(date: Date): Date {
    const m = dayjs(date);
    return m.startOf('month').toDate();
  }

  static endOfTheMonth(date: Date): Date {
    const m = dayjs(date);
    return m.endOf('month').toDate();
  }

  static addTimeToDate(date: Date, time: string) {
    const split = time.split(':');
    if (split.length >= 2) {
      const hours = parseFloat(split[0]);
      const minutes = parseFloat(split[1]);
      if (split.length === 3) {
        const sec = parseFloat(split[2]);
        date.setHours(hours, minutes, sec);
      } else {
        date.setHours(hours, minutes);
      }
    }
  }

  static compareDate(date1: Date, date2: Date) {
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
    );
  }

  static diffDMY(date1: Date, date2: Date) {
    const d1 = dayjs(
      new Date(date1.getFullYear(), date1.getMonth(), date1.getDate()),
    );
    const d2 = dayjs(
      new Date(date2.getFullYear(), date2.getMonth(), date2.getDate()),
    );
    return d1.diff(d2, 'day');
  }

  static isToday(date: Date) {
    return CommonUtils.compareDate(new Date(), date);
  }

  static convertSecondToTimeString(time: number): string {
    const hours = Math.floor(time / 3600);
    const minutes = Math.floor((time % 3600) / 60);
    return `${hours < 10 ? '0' + hours : hours}:${
      minutes < 10 ? '0' + minutes : minutes
    }`;
  }

  static addDayToDate(date: Date, amount: number): Date {
    let m = dayjs(date);
    m = m.add(amount, 'd');
    return m.toDate();
  }

  static addWeekToDate(date: Date, amount: number): Date {
    // Todo :: weird tests
    const m = dayjs(date);
    m.add(amount, 'w');
    return m.toDate();
  }

  static isBetween(
    date: Date,
    start: Date,
    end: Date,
    granularity: OpUnitType = 'day',
    inclusivity?: '()' | '[]' | '[)' | '(]',
  ): boolean {
    const md = dayjs(date);
    const mdS = dayjs(start);
    const mdE = dayjs(end);
    return md.isBetween(mdS, mdE, granularity, inclusivity);
  }

  /**
   * return the day of the week
   * @param date
   */
  public static getISODay(date: Date): number {
    return dayjs(date).isoWeekday();
  }

  public static getWeekCountOfMonth(date: Date): number {
    const start = dayjs(date).startOf('month').startOf('isoWeek');
    const end = dayjs(date).endOf('month').endOf('isoWeek');
    return end.diff(start, 'week') + 1;
  }

  public static convertDateToDateTimeString(
    date: Date,
    second?: boolean,
  ): string {
    if (second) {
      return CommonUtils.pipe.transform(date, 'dd/MM/yyyy HH:mm:ss');
    } else {
      return CommonUtils.pipe.transform(date, 'dd/MM/yyyy HH:mm');
    }
  }

  public static random(max: number, min = 0, decimal = 0): number {
    const rand = Math.random() * (max - min) + min;
    return parseFloat(rand.toFixed(decimal));
  }

  public static uuid(): string {
    return uuid.v7();
  }

  /**
   * Get a color based on the provided color type.
   * @param value
   */
  public static color(value: ColorType): string {
    const color = getComputedStyle(document.documentElement)
      .getPropertyValue(`--${value}`)
      .trim();
    if (color) {
      return color;
    }
    return '#2f373a'; // Default to black if not found
  }

  public static applyProps(from: any, to: unknown) {
    for (const prop in from) {
      if (to[prop] === undefined) {
        to[prop] = from[prop];
      }
    }
  }

  public static addUrlParam(url: string, key: string, value: string): string {
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}${key}=${encodeURIComponent(value)}`;
  }

  public static toCssUnit(value: number | string): string {
    return typeof value === 'number' ? `${value}px` : value;
  }

  public static iconByCheckState(state: CheckState): string {
    switch (state) {
      case CheckState.check:
        return 'check-square fas';
      case CheckState.partial:
        return 'square-minus far';
      default:
        return 'square far';
    }
  }

  public static colorByCheckState(state: CheckState): string {
    return state === CheckState.uncheck
      ? CommonUtils.color('grey-65')
      : CommonUtils.color('button-blue');
  }
}
